import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiUsers, FiFileText, FiMessageSquare, FiTrendingUp, 
  FiShield, FiAlertCircle, FiActivity,
  FiEye, FiTrash2, FiCheckCircle,
  FiMail, FiClock, FiBarChart2, FiPieChart, FiHeadphones,
  FiAlertTriangle, FiCheck, FiX, FiRefreshCw, FiDownload,
  FiDatabase, FiCpu, FiHardDrive
} from 'react-icons/fi';
import { MdVerified } from 'react-icons/md';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import './AdminDashboard.css';

const normalizeRole = (role) => {
  if (!role) return 'USER';
  const r = String(role);
  const map = {
    superadmin: 'SUPERADMIN',
    ceo: 'CEO',
    COO: 'COO',
    coo: 'COO',
    admin: 'ADMIN',
    graphyra_team: 'GRAPHYRA_TEAM',
    head_of_content: 'CONTENT_DIRECTOR',
    content_director: 'CONTENT_DIRECTOR',
    CONTENT_DIRECTOR: 'CONTENT_DIRECTOR',
    community_lead: 'COMMUNITY_LEAD',
    COMMUNITY_LEAD: 'COMMUNITY_LEAD',
    moderator_senior: 'MODERATOR_SENIOR',
    MODERATOR_SENIOR: 'MODERATOR_SENIOR',
    moderator: 'MODERATOR',
    MODERATOR: 'MODERATOR',
    support: 'SUPPORT',
    SUPPORT: 'SUPPORT',
    engineer: 'ENGINEER',
    ENGINEER: 'ENGINEER',
    reviewer: 'REVIEWER',
    REVIEWER: 'REVIEWER',
    official_account: 'OFFICIAL_ACCOUNT',
    OFFICIAL_ACCOUNT: 'OFFICIAL_ACCOUNT'
  };
  return map[r] || r.toUpperCase();
};

const canAccessAdminDashboard = (role) => {
  const nr = normalizeRole(role);
  return ['SUPERADMIN', 'CEO', 'ADMIN', 'GRAPHYRA_TEAM', 'COO', 'CONTENT_DIRECTOR', 'COMMUNITY_LEAD', 'MODERATOR_SENIOR', 'MODERATOR', 'SUPPORT', 'ENGINEER', 'REVIEWER'].includes(nr);
};

const canManageUsers = (role) => {
  const nr = normalizeRole(role);
  return ['SUPERADMIN', 'CEO', 'ADMIN', 'COO', 'MODERATOR_SENIOR'].includes(nr);
};

const canChangeRoles = (role) => {
  const nr = normalizeRole(role);
  return ['SUPERADMIN', 'CEO', 'COO'].includes(nr);
};

const isProtectedTarget = (targetHierarchicalRole, targetVerificationLevel) => {
  const nr = normalizeRole(targetHierarchicalRole);
  return ['SUPERADMIN', 'CEO', 'OFFICIAL_ACCOUNT'].includes(nr) || targetVerificationLevel === 'official';
};

const canManageContent = (role) => {
  const nr = normalizeRole(role);
  return ['SUPERADMIN', 'CEO', 'COO', 'ADMIN', 'GRAPHYRA_TEAM', 'CONTENT_DIRECTOR', 'COMMUNITY_LEAD', 'MODERATOR_SENIOR', 'MODERATOR', 'REVIEWER'].includes(nr);
};

const canManageSupport = (role) => {
  const nr = normalizeRole(role);
  return ['SUPERADMIN', 'CEO', 'COO', 'ADMIN', 'GRAPHYRA_TEAM', 'CONTENT_DIRECTOR', 'COMMUNITY_LEAD', 'MODERATOR_SENIOR', 'MODERATOR', 'SUPPORT', 'REVIEWER'].includes(nr);
};

const canManageSystem = (role) => {
  const nr = normalizeRole(role);
  return ['CEO', 'ENGINEER'].includes(nr);
};

const canDeleteContentPermanently = (role) => normalizeRole(role) === 'CEO';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Estados para estadísticas
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalPosts: 0,
    totalComments: 0,
    totalViews: 0,
    activeUsers: 0,
    pendingReports: 0,
    pendingSupport: 0,
    serverHealth: 100,
    responseTime: 0,
    errorRate: 0
  });

  const [supportTickets, setSupportTickets] = useState([]);
  const [verificationRequests, setVerificationRequests] = useState([]);
  const [reports, setReports] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [systemHealth, setSystemHealth] = useState({});
  const [ticketFilter, setTicketFilter] = useState('all'); // all, open, pending, resolved

  // Users management
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersQuery, setUsersQuery] = useState('');
  const [usersRoleFilter, setUsersRoleFilter] = useState('all');
  const [userDrafts, setUserDrafts] = useState({});

  // Content management
  const [contentPosts, setContentPosts] = useState([]);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentQuery, setContentQuery] = useState('');
  const [contentIncludeHidden, setContentIncludeHidden] = useState(true);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const tasks = [
        { key: 'stats', run: () => adminAPI.getStats() },
        { key: 'activity', run: () => adminAPI.getRecentActivity() }
      ];

      if (canManageSupport(user?.hierarchical_role)) {
        tasks.push({ key: 'support', run: () => adminAPI.getSupportTickets() });
        tasks.push({ key: 'verifications', run: () => adminAPI.getVerificationRequests({ status: 'pending', limit: 50, offset: 0 }) });
      }

      if (canManageContent(user?.hierarchical_role)) {
        tasks.push({ key: 'reports', run: () => adminAPI.getReports() });
      }

      if (canManageSystem(user?.hierarchical_role)) {
        tasks.push({ key: 'system', run: () => adminAPI.getSystemHealth() });
      }

      const results = await Promise.allSettled(tasks.map((t) => t.run()));
      results.forEach((result, idx) => {
        const key = tasks[idx].key;
        if (result.status !== 'fulfilled') return;
        const data = result.value.data;
        if (key === 'stats') setStats(data);
        if (key === 'activity') setRecentActivity(data);
        if (key === 'support') setSupportTickets(data);
        if (key === 'verifications') setVerificationRequests(data);
        if (key === 'reports') setReports(data);
        if (key === 'system') setSystemHealth(data);
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Error al cargar los datos del panel');
    } finally {
      setLoading(false);
    }
  }, [user?.hierarchical_role]);

  useEffect(() => {
    if (!user || !canAccessAdminDashboard(user.hierarchical_role)) {
      toast.error('No tienes permisos para acceder a esta página');
      navigate('/');
      return;
    }

    loadDashboardData();
  }, [user, navigate, loadDashboardData]);

  const refreshData = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
    toast.success('Datos actualizados');
  };

  const handleTicketStatusChange = async (ticketId, newStatus) => {
    try {
      await adminAPI.updateTicketStatus(ticketId, newStatus);
      toast.success('Estado del ticket actualizado');
      loadDashboardData();
    } catch (error) {
      console.error('Error updating ticket:', error);
      toast.error('Error al actualizar el ticket');
    }
  };

  const handleReportAction = async (reportId, action) => {
    try {
      await adminAPI.handleReport(reportId, action);
      toast.success(`Reporte ${action === 'resolve' ? 'resuelto' : 'rechazado'}`);
      loadDashboardData();
    } catch (error) {
      console.error('Error handling report:', error);
      toast.error('Error al procesar el reporte');
    }
  };

  const handleVerificationDecision = async (requestId, decision) => {
    try {
      const note = decision === 'rejected'
        ? (window.prompt('Motivo del rechazo (opcional):') || '')
        : '';
      await adminAPI.updateVerificationRequest(requestId, { status: decision, notes: note || null });
      toast.success(decision === 'approved' ? 'Verificación aprobada' : 'Verificación rechazada');
      loadDashboardData();
    } catch (error) {
      console.error('Error updating verification request:', error);
      toast.error('Error al actualizar la solicitud');
    }
  };

  const exportData = async (type) => {
    try {
      const response = await adminAPI.exportData(type);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Datos de ${type} exportados`);
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Error al exportar datos');
    }
  };

  const loadUsers = useCallback(async () => {
    try {
      if (!canManageUsers(user?.hierarchical_role)) {
        return;
      }
      setUsersLoading(true);
      const params = {
        q: usersQuery?.trim() ? usersQuery.trim() : undefined,
        role: usersRoleFilter !== 'all' ? usersRoleFilter : undefined,
        limit: 50,
        offset: 0
      };
      const res = await adminAPI.getUsers(params);
      setUsers(res.data);

      const drafts = {};
      res.data.forEach((u) => {
        drafts[u.id] = {
          hierarchical_role: u.hierarchical_role,
          verification_level: u.verification_level || 'none',
          can_publish: !!u.can_publish,
          can_respond: !!u.can_respond,
          can_delete: !!u.can_delete
        };
      });
      setUserDrafts(drafts);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setUsersLoading(false);
    }
  }, [user?.hierarchical_role, usersQuery, usersRoleFilter]);

  const updateUserDraft = (userId, patch) => {
    setUserDrafts((prev) => ({
      ...prev,
      [userId]: {
        ...(prev[userId] || {}),
        ...patch
      }
    }));
  };

  const saveUserChanges = async (u) => {
    try {
      const draft = userDrafts[u.id];
      if (!draft) return;

      const normalizedActor = normalizeRole(user?.hierarchical_role);
      const actorIsTop = ['SUPERADMIN', 'CEO'].includes(normalizedActor);
      if (isProtectedTarget(u.hierarchical_role, u.verification_level) && !actorIsTop) {
        toast.error('No puedes modificar Superadmin/CEO/Cuenta Oficial');
        return;
      }

      if (draft.hierarchical_role !== u.hierarchical_role) {
        await adminAPI.updateUserRole(u.id, draft.hierarchical_role);
      }

      const desiredVerification = draft.verification_level === 'none' ? null : draft.verification_level;
      const currentVerification = u.verification_level || null;
      if (desiredVerification !== currentVerification) {
        await adminAPI.updateUserVerification(u.id, desiredVerification);
      }

      if (
        draft.can_publish !== !!u.can_publish ||
        draft.can_respond !== !!u.can_respond ||
        draft.can_delete !== !!u.can_delete
      ) {
        await adminAPI.updateUserPermissions(u.id, {
          can_publish: draft.can_publish,
          can_respond: draft.can_respond,
          can_delete: draft.can_delete
        });
      }

      toast.success('Usuario actualizado');
      await loadUsers();
    } catch (error) {
      console.error('Error saving user changes:', error);
      toast.error(error?.response?.data?.error || 'Error al actualizar usuario');
    }
  };

  const toggleUserBan = async (u) => {
    try {
      await adminAPI.banUser(u.id, !u.is_banned, null);
      toast.success(!u.is_banned ? 'Usuario baneado' : 'Usuario desbaneado');
      await loadUsers();
    } catch (error) {
      console.error('Error banning user:', error);
      toast.error(error?.response?.data?.error || 'Error al actualizar baneo');
    }
  };

  const loadContentPosts = useCallback(async () => {
    try {
      if (!canManageContent(user?.hierarchical_role)) {
        return;
      }
      setContentLoading(true);
      const params = {
        q: contentQuery?.trim() ? contentQuery.trim() : undefined,
        includeHidden: contentIncludeHidden ? 'true' : 'false',
        limit: 50,
        offset: 0
      };
      const res = await adminAPI.getContentPosts(params);
      setContentPosts(res.data);
    } catch (error) {
      console.error('Error loading content posts:', error);
      toast.error('Error al cargar contenido');
    } finally {
      setContentLoading(false);
    }
  }, [user?.hierarchical_role, contentQuery, contentIncludeHidden]);

  useEffect(() => {
    if (!user) return;
    if (activeTab === 'users' && users.length === 0) {
      loadUsers();
    }
    if (activeTab === 'content' && contentPosts.length === 0) {
      loadContentPosts();
    }
  }, [activeTab, user, users.length, contentPosts.length, loadUsers, loadContentPosts]);

  const togglePostHidden = async (p) => {
    try {
      await adminAPI.hidePost(p.id, !p.is_hidden, null);
      toast.success(!p.is_hidden ? 'Publicación ocultada' : 'Publicación visible');
      await loadContentPosts();
    } catch (error) {
      console.error('Error toggling post visibility:', error);
      toast.error(error?.response?.data?.error || 'Error al actualizar visibilidad');
    }
  };

  const deletePost = async (p) => {
    try {
      await adminAPI.deletePost(p.id);
      toast.success('Publicación eliminada');
      await loadContentPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error(error?.response?.data?.error || 'Error al eliminar publicación');
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-loading">
          <div className="admin-spinner"></div>
          <p>Cargando panel de administración...</p>
        </div>
      </div>
    );
  }

  const filteredTickets = supportTickets.filter(ticket => {
    if (ticketFilter === 'all') return true;
    return ticket.status === ticketFilter;
  });

  return (
    <div className="admin-dashboard">
      <div className="admin-container">
        {/* Header */}
        <div className="admin-header">
          <div className="header-left">
            <h1>
              <FiShield className="shield-icon" />
              Panel de Administración
            </h1>
            <p>Gestión completa de Graphyra</p>
          </div>
          <div className="header-right">
            <button 
              className="btn-refresh" 
              onClick={refreshData}
              disabled={refreshing}
            >
              <FiRefreshCw className={refreshing ? 'spinning' : ''} />
              Actualizar
            </button>
            <div className="admin-user-badge">
              <span>{user.hierarchical_role}</span>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="admin-tabs">
          <button 
            className={`admin-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <FiActivity /> Resumen
          </button>
          <button 
            className={`admin-tab ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            <FiBarChart2 /> Analíticas
          </button>
          <button 
            className={`admin-tab ${activeTab === 'support' ? 'active' : ''}`}
            onClick={() => setActiveTab('support')}
          >
            <FiHeadphones /> Soporte 
            {stats.pendingSupport > 0 && <span className="admin-badge">{stats.pendingSupport}</span>}
          </button>
          <button 
            className={`admin-tab ${activeTab === 'reports' ? 'active' : ''}`}
            onClick={() => setActiveTab('reports')}
          >
            <FiAlertCircle /> Reportes
            {stats.pendingReports > 0 && <span className="admin-badge">{stats.pendingReports}</span>}
          </button>
          <button 
            className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <FiUsers /> Usuarios
          </button>
          <button 
            className={`admin-tab ${activeTab === 'content' ? 'active' : ''}`}
            onClick={() => setActiveTab('content')}
          >
            <FiFileText /> Contenido
          </button>
          <button 
            className={`admin-tab ${activeTab === 'system' ? 'active' : ''}`}
            onClick={() => setActiveTab('system')}
          >
            <FiCpu /> Sistema
          </button>
        </div>

        {/* Content Area */}
        <div className="admin-content">
          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <div className="overview-tab">
              {/* Stats Grid */}
              <div className="stats-grid">
                <div className="stat-card users">
                  <div className="stat-header">
                    <FiUsers className="stat-icon" />
                    <span className="stat-label">Usuarios Totales</span>
                  </div>
                  <div className="stat-value">{stats.totalUsers.toLocaleString()}</div>
                  <div className="stat-footer">
                    <span className="stat-change positive">
                      <FiTrendingUp /> +12% este mes
                    </span>
                  </div>
                </div>

                <div className="stat-card posts">
                  <div className="stat-header">
                    <FiFileText className="stat-icon" />
                    <span className="stat-label">Publicaciones</span>
                  </div>
                  <div className="stat-value">{stats.totalPosts.toLocaleString()}</div>
                  <div className="stat-footer">
                    <span className="stat-change positive">
                      <FiTrendingUp /> +8% este mes
                    </span>
                  </div>
                </div>

                <div className="stat-card comments">
                  <div className="stat-header">
                    <FiMessageSquare className="stat-icon" />
                    <span className="stat-label">Comentarios</span>
                  </div>
                  <div className="stat-value">{stats.totalComments.toLocaleString()}</div>
                  <div className="stat-footer">
                    <span className="stat-change positive">
                      <FiTrendingUp /> +15% este mes
                    </span>
                  </div>
                </div>

                <div className="stat-card views">
                  <div className="stat-header">
                    <FiEye className="stat-icon" />
                    <span className="stat-label">Visualizaciones</span>
                  </div>
                  <div className="stat-value">{stats.totalViews.toLocaleString()}</div>
                  <div className="stat-footer">
                    <span className="stat-change positive">
                      <FiTrendingUp /> +25% este mes
                    </span>
                  </div>
                </div>

                <div className="stat-card active">
                  <div className="stat-header">
                    <FiActivity className="stat-icon" />
                    <span className="stat-label">Usuarios Activos</span>
                  </div>
                  <div className="stat-value">{stats.activeUsers.toLocaleString()}</div>
                  <div className="stat-footer">
                    <span className="stat-label-small">Últimas 24h</span>
                  </div>
                </div>

                <div className="stat-card support">
                  <div className="stat-header">
                    <FiHeadphones className="stat-icon" />
                    <span className="stat-label">Tickets Pendientes</span>
                  </div>
                  <div className="stat-value">{stats.pendingSupport}</div>
                  <div className="stat-footer">
                    {stats.pendingSupport > 0 ? (
                      <span className="stat-change negative">
                        <FiAlertCircle /> Requiere atención
                      </span>
                    ) : (
                      <span className="stat-change positive">
                        <FiCheckCircle /> Al día
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="quick-actions">
                <h3>Acciones Rápidas</h3>
                <div className="actions-grid">
                  <button className="admin-action-btn" onClick={() => setActiveTab('support')}>
                    <FiHeadphones />
                    <span>Atender Soporte</span>
                    {stats.pendingSupport > 0 && <span className="admin-badge">{stats.pendingSupport}</span>}
                  </button>
                  <button className="admin-action-btn" onClick={() => setActiveTab('reports')}>
                    <FiAlertCircle />
                    <span>Revisar Reportes</span>
                    {stats.pendingReports > 0 && <span className="admin-badge">{stats.pendingReports}</span>}
                  </button>
                  <button className="admin-action-btn" onClick={() => exportData('users')}>
                    <FiDownload />
                    <span>Exportar Usuarios</span>
                  </button>
                  <button className="admin-action-btn" onClick={() => setActiveTab('analytics')}>
                    <FiBarChart2 />
                    <span>Ver Analíticas</span>
                  </button>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="recent-activity">
                <h3>Actividad Reciente</h3>
                <div className="activity-list">
                  {recentActivity.slice(0, 10).map((activity, index) => (
                    <div key={index} className="activity-item">
                      <div className={`activity-icon ${activity.type}`}>
                        {activity.type === 'user' && <FiUsers />}
                        {activity.type === 'post' && <FiFileText />}
                        {activity.type === 'comment' && <FiMessageSquare />}
                        {activity.type === 'report' && <FiAlertCircle />}
                      </div>
                      <div className="activity-content">
                        <p className="activity-text">{activity.description}</p>
                        <span className="activity-time">{activity.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ANALYTICS TAB */}
          {activeTab === 'analytics' && (
            <div className="analytics-tab">
              <div className="analytics-header">
                <h2>Analíticas Detalladas</h2>
                <div className="analytics-actions">
                  <button className="btn-secondary" onClick={() => exportData('analytics')}>
                    <FiDownload /> Exportar Reporte
                  </button>
                </div>
              </div>

              {/* Charts Grid */}
              <div className="charts-grid">
                <div className="chart-card">
                  <h3>Crecimiento de Usuarios</h3>
                  <div className="chart-placeholder">
                    <FiTrendingUp size={48} />
                    <p>Gráfico de crecimiento de usuarios (últimos 30 días)</p>
                    <div className="chart-stats">
                      <div className="chart-stat">
                        <span className="label">Total:</span>
                        <span className="value">{stats.totalUsers}</span>
                      </div>
                      <div className="chart-stat">
                        <span className="label">Crecimiento:</span>
                        <span className="value positive">+12%</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="chart-card">
                  <h3>Actividad por Hora</h3>
                  <div className="chart-placeholder">
                    <FiActivity size={48} />
                    <p>Usuarios activos por hora del día</p>
                    <div className="chart-stats">
                      <div className="chart-stat">
                        <span className="label">Pico:</span>
                        <span className="value">15:00 - 18:00</span>
                      </div>
                      <div className="chart-stat">
                        <span className="label">Promedio:</span>
                        <span className="value">{Math.floor(stats.activeUsers / 24)}/h</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="chart-card">
                  <h3>Categorías Populares</h3>
                  <div className="chart-placeholder">
                    <FiPieChart size={48} />
                    <p>Distribución de contenido por categoría</p>
                    <div className="category-list">
                      <div className="category-item">
                        <span className="category-name">Ciencia</span>
                        <div className="category-bar" style={{width: '85%'}}></div>
                        <span className="category-value">85%</span>
                      </div>
                      <div className="category-item">
                        <span className="category-name">Tecnología</span>
                        <div className="category-bar" style={{width: '72%'}}></div>
                        <span className="category-value">72%</span>
                      </div>
                      <div className="category-item">
                        <span className="category-name">Educación</span>
                        <div className="category-bar" style={{width: '68%'}}></div>
                        <span className="category-value">68%</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="chart-card">
                  <h3>Engagement</h3>
                  <div className="chart-placeholder">
                    <FiMessageSquare size={48} />
                    <p>Tasa de interacción de usuarios</p>
                    <div className="chart-stats">
                      <div className="chart-stat">
                        <span className="label">Comentarios/Post:</span>
                        <span className="value">{(stats.totalComments / stats.totalPosts).toFixed(1)}</span>
                      </div>
                      <div className="chart-stat">
                        <span className="label">Tasa:</span>
                        <span className="value positive">8.5%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SUPPORT TAB */}
          {activeTab === 'support' && (
            <div className="support-tab">
              <div className="support-header">
                <h2>Tickets de Soporte</h2>
                <div className="support-filters">
                  <button 
                    className={`admin-filter-btn ${ticketFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setTicketFilter('all')}
                  >
                    Todos ({supportTickets.length})
                  </button>
                  <button 
                    className={`admin-filter-btn ${ticketFilter === 'open' ? 'active' : ''}`}
                    onClick={() => setTicketFilter('open')}
                  >
                    Abiertos ({supportTickets.filter(t => t.status === 'open').length})
                  </button>
                  <button 
                    className={`admin-filter-btn ${ticketFilter === 'pending' ? 'active' : ''}`}
                    onClick={() => setTicketFilter('pending')}
                  >
                    Pendientes ({supportTickets.filter(t => t.status === 'pending').length})
                  </button>
                  <button 
                    className={`admin-filter-btn ${ticketFilter === 'resolved' ? 'active' : ''}`}
                    onClick={() => setTicketFilter('resolved')}
                  >
                    Resueltos ({supportTickets.filter(t => t.status === 'resolved').length})
                  </button>
                </div>
              </div>

              <div className="tickets-grid">
                {filteredTickets.length === 0 ? (
                  <div className="admin-empty-state">
                    <FiCheckCircle size={64} />
                    <h3>No hay tickets {ticketFilter !== 'all' ? ticketFilter + 's' : ''}</h3>
                    <p>Todos los tickets están al día</p>
                  </div>
                ) : (
                  filteredTickets.map(ticket => (
                    <div key={ticket.id} className={`ticket-card ${ticket.priority}`}>
                      <div className="ticket-header">
                        <div className="ticket-info">
                          <h4>{ticket.subject}</h4>
                          <div className="ticket-meta">
                            <span className="ticket-id">#{ticket.id}</span>
                            <span className="ticket-category">{ticket.category}</span>
                            <span className={`ticket-priority ${ticket.priority}`}>
                              {ticket.priority === 'high' && <FiAlertTriangle />}
                              {ticket.priority}
                            </span>
                          </div>
                        </div>
                        <div className="ticket-status">
                          <span className={`admin-status-badge ${ticket.status}`}>
                            {ticket.status === 'open' && 'Abierto'}
                            {ticket.status === 'pending' && 'Pendiente'}
                            {ticket.status === 'resolved' && 'Resuelto'}
                          </span>
                        </div>
                      </div>

                      <div className="ticket-body">
                        <p>{ticket.message}</p>
                      </div>

                      <div className="ticket-footer">
                        <div className="ticket-user">
                          <FiUsers />
                          <span>{ticket.userName}</span>
                          <span className="ticket-email">{ticket.userEmail}</span>
                        </div>
                        <div className="ticket-time">
                          <FiClock />
                          <span>{ticket.createdAt}</span>
                        </div>
                      </div>

                      <div className="ticket-actions">
                        {ticket.status !== 'resolved' && (
                          <>
                            <button 
                              className="admin-btn-action primary"
                              onClick={() => handleTicketStatusChange(ticket.id, 'resolved')}
                            >
                              <FiCheck /> Resolver
                            </button>
                            {ticket.status === 'open' && (
                              <button 
                                className="admin-btn-action secondary"
                                onClick={() => handleTicketStatusChange(ticket.id, 'pending')}
                              >
                                <FiClock /> Marcar Pendiente
                              </button>
                            )}
                          </>
                        )}
                        <button className="admin-btn-action">
                          <FiMail /> Responder
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="support-header" style={{ marginTop: 24 }}>
                <h2>Solicitudes de Verificación</h2>
              </div>

              <div className="tickets-grid">
                {verificationRequests.length === 0 ? (
                  <div className="admin-empty-state">
                    <FiCheckCircle size={64} />
                    <h3>No hay solicitudes pendientes</h3>
                    <p>El equipo de soporte está al día</p>
                  </div>
                ) : (
                  verificationRequests.map((vr) => (
                    <div
                      key={vr.id}
                      className="ticket-card medium"
                      style={{ borderLeft: `4px solid ${vr.requested_badge_color || 'var(--border)'}` }}
                    >
                      <div className="ticket-header">
                        <div className="ticket-info">
                          <h4 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <FiCheckCircle style={{ color: vr.requested_badge_color || 'var(--text-secondary)' }} />
                            Solicitud: {vr.requested_badge_label || vr.requested_level}
                          </h4>
                          <div className="ticket-meta">
                            <span className="ticket-id">#{vr.id}</span>
                            <span className="ticket-category">@{vr.username}</span>
                            {vr.role_label && <span className="ticket-category">{vr.role_label}</span>}
                            <span className={`admin-status-badge ${vr.status}`}>
                              {vr.status === 'pending' && <><FiClock /> Pendiente</>}
                              {vr.status === 'under_review' && <><FiEye /> En revisión</>}
                              {vr.status === 'approved' && <><FiCheckCircle /> Aprobada</>}
                              {vr.status === 'rejected' && <><FiX /> Rechazada</>}
                              {!['pending', 'under_review', 'approved', 'rejected'].includes(vr.status) && vr.status}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="ticket-body">
                        <p>{vr.notes || 'Sin notas.'}</p>
                      </div>

                      <div className="ticket-footer">
                        <div className="ticket-user">
                          <FiUsers />
                          <span>{vr.name || vr.username}</span>
                          <span className="ticket-email">{vr.email}</span>
                        </div>
                        <div className="ticket-time">
                          <FiClock />
                          <span>{vr.created_at || vr.createdAt || ''}</span>
                        </div>
                      </div>

                      <div className="ticket-actions">
                        <button
                          className="admin-btn-action primary"
                          onClick={() => handleVerificationDecision(vr.id, 'approved')}
                        >
                          <FiCheck /> Aprobar
                        </button>
                        <button
                          className="admin-btn-action secondary"
                          onClick={() => handleVerificationDecision(vr.id, 'rejected')}
                        >
                          <FiX /> Rechazar
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* REPORTS TAB */}
          {activeTab === 'reports' && (
            <div className="reports-tab">
              <div className="reports-header">
                <h2>Reportes de Contenido</h2>
                <p>Revisa y toma acción sobre contenido reportado por usuarios</p>
              </div>

              <div className="reports-grid">
                {reports.length === 0 ? (
                  <div className="admin-empty-state">
                    <FiCheckCircle size={64} />
                    <h3>No hay reportes pendientes</h3>
                    <p>Todo el contenido está en orden</p>
                  </div>
                ) : (
                  reports.map(report => (
                    <div key={report.id} className="report-card">
                      <div className="report-header">
                        <div className="report-type">
                          {report.type === 'post' && <FiFileText />}
                          {report.type === 'comment' && <FiMessageSquare />}
                          {report.type === 'user' && <FiUsers />}
                          <span>{report.type === 'post' ? 'Publicación' : report.type === 'comment' ? 'Comentario' : 'Usuario'}</span>
                        </div>
                        <span className={`report-reason ${report.reason}`}>
                          {report.reason}
                        </span>
                      </div>

                      <div className="report-content">
                        <p className="report-description">{report.description}</p>
                        <div className="reported-content">
                          <strong>Contenido reportado:</strong>
                          <p>{report.content}</p>
                        </div>
                      </div>

                      <div className="report-meta">
                        <div className="reporter-info">
                          <FiUsers />
                          <span>Reportado por: {report.reporterName}</span>
                        </div>
                        <div className="report-time">
                          <FiClock />
                          <span>{report.createdAt}</span>
                        </div>
                      </div>

                      <div className="report-actions">
                        <button 
                          className="admin-btn-action danger"
                          onClick={() => handleReportAction(report.id, 'remove')}
                        >
                          <FiTrash2 /> Eliminar Contenido
                        </button>
                        <button 
                          className="admin-btn-action warning"
                          onClick={() => handleReportAction(report.id, 'warn')}
                        >
                          <FiAlertTriangle /> Advertir Usuario
                        </button>
                        <button 
                          className="admin-btn-action secondary"
                          onClick={() => handleReportAction(report.id, 'dismiss')}
                        >
                          <FiX /> Descartar
                        </button>
                        <button 
                          className="admin-btn-action primary"
                          onClick={() => handleReportAction(report.id, 'resolve')}
                        >
                          <FiCheck /> Resolver
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* USERS TAB */}
          {activeTab === 'users' && (
            <div className="users-tab">
              <div className="admin-section-header">
                <h2>Gestión de Usuarios</h2>
                <div className="admin-section-actions">
                  <button className="btn-secondary" onClick={() => exportData('users')}>
                    <FiDownload /> Exportar
                  </button>
                </div>
              </div>

              {!canManageUsers(user?.hierarchical_role) ? (
                <div className="admin-empty-state">
                  <FiAlertTriangle size={64} />
                  <h3>Acceso restringido</h3>
                  <p>No tienes permisos para gestionar usuarios</p>
                </div>
              ) : (
                <>
                  <div className="admin-controls">
                    <input
                      className="admin-input"
                      placeholder="Buscar por nombre, email o usuario"
                      value={usersQuery}
                      onChange={(e) => setUsersQuery(e.target.value)}
                    />
                    <select
                      className="admin-select"
                      value={usersRoleFilter}
                      onChange={(e) => setUsersRoleFilter(e.target.value)}
                    >
                      <option value="all">Todos los roles</option>
                      <option value="superadmin">Superadmin</option>
                      <option value="ceo">CEO</option>
                      <option value="admin">Admin</option>
                      <option value="graphyra_team">Equipo Graphyra</option>
                      <option value="coo">COO</option>
                      <option value="head_of_content">Director de Contenido</option>
                      <option value="community_lead">Community Lead</option>
                      <option value="moderator_senior">Moderador Senior</option>
                      <option value="moderator">Moderador</option>
                      <option value="support">Soporte</option>
                      <option value="engineer">Ingeniero</option>
                      <option value="official_account">Cuenta Oficial</option>
                      <option value="user">Usuario</option>
                    </select>
                    <button className="btn-secondary" onClick={loadUsers} disabled={usersLoading}>
                      <FiRefreshCw className={usersLoading ? 'spinning' : ''} /> Buscar
                    </button>
                  </div>

                  {usersLoading ? (
                    <div className="admin-loading-inline">
                      <div className="admin-spinner"></div>
                      <p>Cargando usuarios...</p>
                    </div>
                  ) : users.length === 0 ? (
                    <div className="admin-empty-state">
                      <FiUsers size={64} />
                      <h3>No hay resultados</h3>
                      <p>Prueba cambiando los filtros</p>
                    </div>
                  ) : (
                    <div className="admin-table-wrap">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Usuario</th>
                            <th>Email</th>
                            <th>Rol</th>
                            <th>Chulito</th>
                            <th>Permisos</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.map((u) => {
                            const draft = userDrafts[u.id] || {};
                            const actorRole = normalizeRole(user?.hierarchical_role);
                            const actorIsTop = ['SUPERADMIN', 'CEO'].includes(actorRole);
                            const roleDisabled = !canChangeRoles(user?.hierarchical_role);
                            const isCeoTarget = normalizeRole(u.hierarchical_role) === 'CEO';
                            const protectedTarget = isProtectedTarget(u.hierarchical_role, u.verification_level);
                            return (
                              <tr key={u.id} className={u.is_banned ? 'is-banned' : ''}>
                                <td>{u.id}</td>
                                <td>
                                  <div className="admin-cell-strong">
                                    {u.name || '-'}
                                    {u.badge_color && (
                                      <MdVerified 
                                        size={16} 
                                        style={{ color: u.badge_color, marginLeft: 6, verticalAlign: 'middle' }} 
                                        title={u.badge_label || 'Verificado'}
                                      />
                                    )}
                                  </div>
                                  <div className="admin-cell-muted">@{u.username || '-'}</div>
                                </td>
                                <td>
                                  <div className="admin-cell-strong">{u.email}</div>
                                  <div className="admin-cell-muted">
                                    {u.email_verified ? 'Email verificado' : 'Email no verificado'} · {u.phone_verified ? 'Tel. verificado' : 'Tel. no verificado'}
                                  </div>
                                </td>
                                <td>
                                  <select
                                    className="admin-select"
                                    value={draft.hierarchical_role ?? u.hierarchical_role}
                                    onChange={(e) => updateUserDraft(u.id, { hierarchical_role: e.target.value })}
                                    disabled={roleDisabled || isCeoTarget || (protectedTarget && !actorIsTop)}
                                  >
                                    <option value="superadmin">superadmin</option>
                                    <option value="ceo">ceo</option>
                                    <option value="admin">admin</option>
                                    <option value="graphyra_team">graphyra_team</option>
                                    <option value="coo">coo</option>
                                    <option value="head_of_content">head_of_content</option>
                                    <option value="community_lead">community_lead</option>
                                    <option value="moderator_senior">moderator_senior</option>
                                    <option value="moderator">moderator</option>
                                    <option value="support">support</option>
                                    <option value="engineer">engineer</option>
                                    <option value="official_account">official_account</option>
                                    <option value="user">user</option>
                                  </select>
                                </td>
                                <td>
                                  <select
                                    className="admin-select"
                                    value={draft.verification_level ?? (u.verification_level || 'none')}
                                    onChange={(e) => updateUserDraft(u.id, { verification_level: e.target.value })}
                                    disabled={(protectedTarget && !actorIsTop)}
                                  >
                                    <option value="none">Sin chulito</option>
                                    <option value="basic">Verificado (gris)</option>
                                    <option value="professional">Profesional (verde)</option>
                                    <option value="academic">Académico (azul)</option>
                                    <option value="institutional">Institución (púrpura)</option>
                                    <option value="reviewer">Revisor (turquesa)</option>
                                    <option value="moderator">Moderador (naranja)</option>
                                    <option value="graphyra_team">Equipo Graphyra (celeste)</option>
                                    <option value="admin">Admin (rojo)</option>
                                    <option value="official" disabled={!actorIsTop}>
                                      Cuenta Oficial Graphyra (rojo)
                                    </option>
                                  </select>
                                </td>
                                <td>
                                  <label className="admin-checkbox">
                                    <input
                                      type="checkbox"
                                      checked={!!draft.can_publish}
                                      onChange={(e) => updateUserDraft(u.id, { can_publish: e.target.checked })}
                                      disabled={isCeoTarget || (protectedTarget && !actorIsTop)}
                                    />
                                    <span>Publicar</span>
                                  </label>
                                  <label className="admin-checkbox">
                                    <input
                                      type="checkbox"
                                      checked={!!draft.can_respond}
                                      onChange={(e) => updateUserDraft(u.id, { can_respond: e.target.checked })}
                                      disabled={isCeoTarget || (protectedTarget && !actorIsTop)}
                                    />
                                    <span>Responder</span>
                                  </label>
                                  <label className="admin-checkbox">
                                    <input
                                      type="checkbox"
                                      checked={!!draft.can_delete}
                                      onChange={(e) => updateUserDraft(u.id, { can_delete: e.target.checked })}
                                      disabled={!['SUPERADMIN', 'CEO'].includes(actorRole) || isCeoTarget}
                                    />
                                    <span>Borrar</span>
                                  </label>
                                </td>
                                <td>
                                  <span className={`admin-status-badge ${u.is_banned ? 'pending' : 'resolved'}`}>
                                    {u.is_banned ? 'Baneado' : 'Activo'}
                                  </span>
                                </td>
                                <td>
                                  <div className="admin-row-actions">
                                    <button
                                      className={`admin-btn-action ${u.is_banned ? 'secondary' : 'danger'}`}
                                      onClick={() => toggleUserBan(u)}
                                      disabled={isCeoTarget || (protectedTarget && !actorIsTop)}
                                    >
                                      {u.is_banned ? <FiCheck /> : <FiX />} {u.is_banned ? 'Desbanear' : 'Banear'}
                                    </button>
                                    <button
                                      className="admin-btn-action primary"
                                      onClick={() => saveUserChanges(u)}
                                      disabled={isCeoTarget || (protectedTarget && !actorIsTop)}
                                    >
                                      <FiCheck /> Guardar
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* CONTENT TAB */}
          {activeTab === 'content' && (
            <div className="content-tab">
              <div className="admin-section-header">
                <h2>Gestión de Contenido</h2>
              </div>

              {!canManageContent(user?.hierarchical_role) ? (
                <div className="admin-empty-state">
                  <FiAlertTriangle size={64} />
                  <h3>Acceso restringido</h3>
                  <p>No tienes permisos para gestionar contenido</p>
                </div>
              ) : (
                <>
                  <div className="admin-controls">
                    <input
                      className="admin-input"
                      placeholder="Buscar por título o slug"
                      value={contentQuery}
                      onChange={(e) => setContentQuery(e.target.value)}
                    />
                    <label className="admin-checkbox inline">
                      <input
                        type="checkbox"
                        checked={contentIncludeHidden}
                        onChange={(e) => setContentIncludeHidden(e.target.checked)}
                      />
                      <span>Incluir ocultos</span>
                    </label>
                    <button className="btn-secondary" onClick={loadContentPosts} disabled={contentLoading}>
                      <FiRefreshCw className={contentLoading ? 'spinning' : ''} /> Buscar
                    </button>
                  </div>

                  {contentLoading ? (
                    <div className="admin-loading-inline">
                      <div className="admin-spinner"></div>
                      <p>Cargando contenido...</p>
                    </div>
                  ) : contentPosts.length === 0 ? (
                    <div className="admin-empty-state">
                      <FiFileText size={64} />
                      <h3>No hay resultados</h3>
                      <p>Prueba cambiando los filtros</p>
                    </div>
                  ) : (
                    <div className="admin-table-wrap">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Título</th>
                            <th>Tipo</th>
                            <th>Categoría</th>
                            <th>Autor</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {contentPosts.map((p) => (
                            <tr key={p.id} className={p.is_hidden ? 'is-hidden' : ''}>
                              <td>{p.id}</td>
                              <td>
                                <div className="admin-cell-strong">{p.title}</div>
                                <div className="admin-cell-muted">{p.slug}</div>
                              </td>
                              <td>{p.type || '-'}</td>
                              <td>{p.category || '-'}</td>
                              <td>
                                <div className="admin-cell-strong">{p.author_name || '-'}</div>
                                <div className="admin-cell-muted">@{p.author_username || '-'}</div>
                              </td>
                              <td>
                                <span className={`admin-status-badge ${p.is_hidden ? 'pending' : 'resolved'}`}>
                                  {p.is_hidden ? 'Oculta' : 'Visible'}
                                </span>
                              </td>
                              <td>
                                <div className="admin-row-actions">
                                  <button
                                    className={`admin-btn-action ${p.is_hidden ? 'secondary' : 'warning'}`}
                                    onClick={() => togglePostHidden(p)}
                                  >
                                    <FiEye /> {p.is_hidden ? 'Mostrar' : 'Ocultar'}
                                  </button>
                                  {canDeleteContentPermanently(user?.hierarchical_role) && (
                                    <button
                                      className="admin-btn-action danger"
                                      onClick={() => deletePost(p)}
                                    >
                                      <FiTrash2 /> Eliminar
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* SYSTEM TAB */}
          {activeTab === 'system' && (
            <div className="system-tab">
              <div className="system-header">
                <h2>Estado del Sistema</h2>
                <div className="system-health">
                  <span className={`health-indicator ${systemHealth.status}`}>
                    {systemHealth.status === 'healthy' ? <FiCheckCircle /> : <FiAlertCircle />}
                    {systemHealth.status === 'healthy' ? 'Saludable' : 'Problemas Detectados'}
                  </span>
                </div>
              </div>

              <div className="system-grid">
                <div className="system-card">
                  <div className="system-icon">
                    <FiCpu />
                  </div>
                  <div className="system-info">
                    <h4>CPU</h4>
                    <div className="system-metric">
                      <span className="metric-value">{systemHealth.cpu || 0}%</span>
                      <div className="metric-bar">
                        <div className="metric-fill" style={{width: `${systemHealth.cpu || 0}%`}}></div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="system-card">
                  <div className="system-icon">
                    <FiHardDrive />
                  </div>
                  <div className="system-info">
                    <h4>Memoria</h4>
                    <div className="system-metric">
                      <span className="metric-value">{systemHealth.memory || 0}%</span>
                      <div className="metric-bar">
                        <div className="metric-fill" style={{width: `${systemHealth.memory || 0}%`}}></div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="system-card">
                  <div className="system-icon">
                    <FiDatabase />
                  </div>
                  <div className="system-info">
                    <h4>Base de Datos</h4>
                    <div className="system-metric">
                      <span className="metric-value">{systemHealth.database || 'Conectada'}</span>
                      <span className="metric-status success">
                        <FiCheckCircle /> Operativa
                      </span>
                    </div>
                  </div>
                </div>

                <div className="system-card">
                  <div className="system-icon">
                    <FiActivity />
                  </div>
                  <div className="system-info">
                    <h4>Tiempo de Respuesta</h4>
                    <div className="system-metric">
                      <span className="metric-value">{systemHealth.responseTime || 0}ms</span>
                      <span className="metric-status success">
                        <FiCheckCircle /> Excelente
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="system-logs">
                <h3>Logs del Sistema</h3>
                <div className="logs-container">
                  {systemHealth.logs && systemHealth.logs.map((log, index) => (
                    <div key={index} className={`log-entry ${log.level}`}>
                      <span className="log-time">{log.timestamp}</span>
                      <span className={`log-level ${log.level}`}>{log.level}</span>
                      <span className="log-message">{log.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
